// =============================================================================
// アイテム・インベントリ・クラフト。純粋なロジック（UIはhud.ts）。
// =============================================================================
import { eat } from './survival';
import type { ItemDef, Recipe } from './types';

// アイテム定義（採取素材＋クラフト品）
export const ITEMS: Record<string, ItemDef> = {
  wood:        { name: '木材',       icon: '🪵' },
  stone:       { name: '石',         icon: '🪨' },
  fiber:       { name: '繊維',       icon: '🌿' },
  coconut:     { name: 'ココナッツ', icon: '🥥', food: { hunger: 25, thirst: 15 } },
  rope:        { name: 'ロープ',     icon: '🧵', use: '道具・いかだ・修理の材料' },
  plank:       { name: '板',         icon: '📏', use: '給水器・いかだ・船の修理に' },
  stone_knife: { name: '石ナイフ',   icon: '🔪', use: '採取で繊維+1' },
  axe:         { name: '石斧',       icon: '🪓', use: '採取で木材+1' },
  campfire:    { name: '焚き火',     icon: '🔥', use: '朝まで休める・調理できる' },
  water_still: { name: '給水器',     icon: '💧', use: '設置すると真水が溜まる' },
  shelter:     { name: 'シェルター', icon: '🛖', use: '我が家。休むと体力全回復' },
  raft_floor:  { name: 'いかだ床',   icon: '🛶', use: '海辺に置いて乗り、島々へ渡る' },
  fishing_rod: { name: '釣り竿',     icon: '🎣', use: '装備して水辺で釣り' },
  fish:        { name: '魚',         icon: '🐟', food: { hunger: 35, health: 4 } },
  spear:       { name: '銛',         icon: '🔱', use: '装備してサメと戦う' },
  torch:       { name: '松明',       icon: '🕯', use: '夜、あたりを照らす' },
  shark_meat:  { name: 'サメ肉',     icon: '🥩', food: { hunger: 55, health: 8 } },
  cooked_fish: { name: '焼き魚',     icon: '🍢', food: { hunger: 55, health: 12 }, use: '満腹+55 体力+12' },
  cooked_meat: { name: '焼きサメ肉', icon: '🍖', food: { hunger: 85, health: 20 }, use: '満腹+85 体力+20' },
  // レア素材(島の秘宝): 各島で採取していると稀に見つかる。島の💎財宝には確定で1つ眠る。
  pearl:       { name: '真珠',       icon: '🦪', use: '南西の環礁の秘宝。御守の材料' },
  obsidian:    { name: '黒曜石',     icon: '🌑', use: '北の山岳島の秘宝。銛を強化できる' },
  bloom:       { name: '楽園の花',   icon: '🌺', use: '南東の高島の秘宝。癒しの茶になる' },
  obsidian_spear: { name: '黒曜石の銛', icon: '🗡', use: '銛の強化版——威力1.6倍' },
  pearl_charm: { name: '真珠の御守', icon: '📿', use: '持っていると酸素が長持ちする' },
  flower_tea:  { name: '花の癒し茶', icon: '🍵', food: { health: 40, thirst: 25 }, use: '体力+40 水分+25' },
};

// クラフトレシピ
export const RECIPES: Recipe[] = [
  { id: 'rope',        out: { item: 'rope', count: 1 },        cost: { fiber: 2 } },
  { id: 'plank',       out: { item: 'plank', count: 1 },       cost: { wood: 2 } },
  { id: 'stone_knife', out: { item: 'stone_knife', count: 1 }, cost: { stone: 2, wood: 1 } },
  { id: 'axe',         out: { item: 'axe', count: 1 },         cost: { stone: 1, wood: 2, rope: 1 } },
  { id: 'campfire',    out: { item: 'campfire', count: 1 },    cost: { wood: 4, stone: 3 } },
  { id: 'water_still', out: { item: 'water_still', count: 1 }, cost: { plank: 2, rope: 1 } },
  { id: 'shelter',     out: { item: 'shelter', count: 1 },     cost: { plank: 6, rope: 4, fiber: 6 } },
  { id: 'raft_floor',  out: { item: 'raft_floor', count: 1 },  cost: { plank: 4, rope: 2 } },
  { id: 'fishing_rod', out: { item: 'fishing_rod', count: 1 }, cost: { wood: 2, rope: 1 } },
  { id: 'spear',       out: { item: 'spear', count: 1 },       cost: { wood: 2, stone: 1, fiber: 2 } },
  { id: 'torch',       out: { item: 'torch', count: 1 },       cost: { wood: 1, fiber: 2 } },
  { id: 'cook_fish',   out: { item: 'cooked_fish', count: 1 }, cost: { fish: 1 },       station: 'campfire' },
  { id: 'cook_meat',   out: { item: 'cooked_meat', count: 1 }, cost: { shark_meat: 1 }, station: 'campfire' },
  // レア素材の上位クラフト(秘宝を力に変える)
  { id: 'obsidian_spear', out: { item: 'obsidian_spear', count: 1 }, cost: { spear: 1, obsidian: 1 } },
  { id: 'pearl_charm',    out: { item: 'pearl_charm', count: 1 },    cost: { pearl: 1, rope: 1 } },
  { id: 'flower_tea',     out: { item: 'flower_tea', count: 1 },     cost: { bloom: 1 }, station: 'campfire' },
];

export const inventory: Record<string, number> = Object.create(null);

export function add(item: string, count = 1): number {
  inventory[item] = (inventory[item] || 0) + count;
  return inventory[item];
}
export function count(item: string): number { return inventory[item] || 0; }

export function canCraft(recipe: Recipe): boolean {
  for (const k in recipe.cost) if (count(k) < recipe.cost[k]) return false;
  return true;
}

export function craft(recipeId: string): boolean {
  const r = RECIPES.find((x) => x.id === recipeId);
  if (!r || !canCraft(r)) return false;
  for (const k in r.cost) inventory[k] -= r.cost[k];
  add(r.out.item, r.out.count);
  return true;
}

// 食べる/飲む。成功で true、消費。
export function consume(item: string): boolean {
  const def = ITEMS[item];
  if (!def || !def.food || count(item) <= 0) return false;
  inventory[item] -= 1;
  eat(def.food);
  return true;
}
